/**
 * Search metadata for every public route — ONE source for the pages (which set
 * it in the browser) and the server renderer (api/render.js, which prints it
 * into the HTML), so the raw HTML and the rendered page always agree
 * (2026-10-17). Pure: no React, no browser APIs.
 *
 * Rules (checked by server/scripts/qa/prerender.test.js): every title ≤ 60
 * characters, every description ≤ 160, unique per route; canonical is
 * absolute, https://www., no trailing slash except the homepage.
 */
import { SITE } from '../data/blogConfig';
import LOCATIONS, { getLocation } from '../data/locations';
import { SERVICE_DETAIL } from '../data/services';

export function canonicalFor(path) {
  const p = String(path || '/').split(/[?#]/)[0];
  if (p === '/' || p === '') return `${SITE}/`;
  return `${SITE}${p.replace(/\/+$/, '')}`;
}

/** Fixed pages. `h1` and `intro` are what the server prints before React loads. */
export const STATIC_ROUTES = {
  '/': {
    title: 'Utah Real Estate Photography | Drone, Aerial & Video Tours',
    description: 'Real estate photography for Utah agents and home sellers: HDR photos, aerial, twilight, 3D tours and video, with fixed published prices and fast delivery.',
    h1: 'Professional Real Estate Photography in Utah',
    intro: 'Professional photography for realtors and home sellers. Drone shots, twilight photography, and cinematic video tours with fast turnaround.'
  },
  '/services': {
    title: 'Real Estate Photography Services & Packages | Utah',
    description: 'Listing photography packages and add-ons for Utah agents: HDR photos, aerial, twilight, video, Matterport 3D tours, virtual staging and land photography.',
    h1: 'Photography Packages',
    intro: 'Professional real estate photography for every listing. One-time booking, no commitment.'
  },
  '/pricing': {
    title: 'Real Estate Photography Pricing in Utah | URP',
    description: 'Every package and add-on price for Utah real estate photography, published up front: photos, aerial, twilight, video, 3D tours, staging and land.',
    h1: 'Pricing',
    intro: 'Fixed package prices, published in full. Pick a package, add what the listing needs, and see the total before you book.'
  },
  '/faq': {
    title: 'Real Estate Photography FAQ | Utah Realty Photos',
    description: 'Pricing, turnaround, gallery access, service area, travel fees and 3D tours: the questions Utah agents ask most about listing photography, answered.',
    h1: 'Real estate photography, answered',
    intro: 'Pricing, turnaround, service area and what to expect on shoot day.'
  },
  '/portfolio': {
    title: 'Real Estate Photography Portfolio | Utah Realty Photos',
    description: 'Recent real estate photography from Utah County and the Wasatch Front: interiors, exteriors, aerial, twilight and video from real listings we shot.',
    h1: 'Portfolio',
    intro: 'Explore our professional real estate photography'
  },
  '/contact': {
    title: 'Contact Utah Realty Photos | Get a Quote',
    description: 'Contact Utah Realty Photos about a listing, a commercial property or a custom shoot. Call, text or email for a same-day quote and fast turnaround.',
    h1: 'Contact Us',
    intro: 'Ready to showcase your property with stunning photography?'
  },
  '/book': {
    title: 'Book Real Estate Photography | Utah Realty Photos',
    description: 'Book a real estate photography shoot online in a few minutes: choose a package and add-ons, see the full price with travel, and pick an open time.',
    h1: 'Book a Photo Session',
    intro: 'Professional real estate photography made easy'
  },
  '/credit': {
    title: 'Prepaid Photography Credit for Agents | Utah Realty Photos',
    description: 'Buy real estate photography credit up front and save on every listing: no monthly commitment, no expiry, no fees. Volume rates for Utah brokerages.',
    h1: 'Pay ahead, pay less',
    intro: 'Buy photography credit up front and save on every listing. No monthly commitment, no expiry, no fees.'
  },
  '/blog': {
    title: 'Real Estate Photography Blog | Utah Realty Photos',
    description: 'Practical guides for Utah agents and sellers: getting a home ready for photos, what listing media costs, and what helps a listing stand out online.',
    h1: 'Photography Blog',
    intro: "Tips, insights, and stories from Utah's premier real estate photographers"
  },
  '/locations': {
    title: 'Service Areas | Utah Real Estate Photography Coverage',
    description: 'Real estate photography across Utah County, Salt Lake, Summit and Weber counties: Eagle Mountain, Lehi, Saratoga Springs, Provo, Orem, SLC and more.',
    h1: 'Service Areas',
    intro: 'Listing media across Utah County, Salt Lake, Summit, and Weber'
  },
  '/about': {
    title: 'About Thomas | Utah Realty Photos, Eagle Mountain',
    description: 'Thomas owns Utah Realty Photos and shoots every listing himself, from Eagle Mountain across Utah County and the Wasatch Front. Fixed prices, fast delivery.',
    h1: 'About Thomas',
    intro: 'The person behind every Utah Realty Photos shoot'
  },
  '/privacy-policy': {
    title: 'Privacy Policy | Utah Realty Photos',
    description: 'What Utah Realty Photos collects when you browse, book or pay, why we collect it, who processes it, and how to ask us to change or delete your data.',
    h1: 'Privacy Policy',
    intro: ''
  },
  '/cookie-policy': {
    title: 'Cookie Policy | Utah Realty Photos',
    description: 'The cookies and browser storage utahrealtyphotos.com uses, what each one does, and how to turn analytics and advertising cookies off at any time.',
    h1: 'Cookie Policy',
    intro: ''
  }
};

export function locationMeta(loc) {
  return {
    title: `Real Estate Photography in ${loc.name}, Utah`,
    description: `Listing photos, aerial, twilight, 3D tours and video for ${loc.name} real estate. Fixed published prices, online booking and fast delivery.`,
    h1: `Real Estate Photography in ${loc.name}`,
    intro: loc.tagline
  };
}

export function serviceMeta(type) {
  const s = SERVICE_DETAIL[type];
  if (!s) return null;
  return { title: s.meta.title, description: s.meta.description, h1: s.title, intro: s.tagline };
}

export function getLocationBySlug(slug) {
  return getLocation(slug) || null;
}

export { LOCATIONS };

/** Meta for a path, or null when the path isn't a known public page. */
export function metaFor(path) {
  const p = String(path || '/').split(/[?#]/)[0].replace(/(.)\/+$/, '$1') || '/';
  let m = STATIC_ROUTES[p] || null;
  let mm;
  if (!m && (mm = p.match(/^\/services\/([a-z0-9-]+)$/))) m = serviceMeta(mm[1]);
  if (!m && (mm = p.match(/^\/locations\/([a-z0-9-]+)$/))) {
    const loc = getLocationBySlug(mm[1]);
    m = loc ? locationMeta(loc) : null;
  }
  return m ? { ...m, canonical: canonicalFor(p) } : null;
}

/** Meta for a blog post (the API's public post shape). */
export function postMeta(post) {
  const clamp = (v, n) => {
    const t = String(v || '').replace(/\s+/g, ' ').trim();
    if (t.length <= n) return t;
    const cut = t.slice(0, n + 1);
    const i = cut.lastIndexOf(' ');
    return (i > n * 0.5 ? cut.slice(0, i) : t.slice(0, n)).replace(/[\s|,;:–—-]+$/, '');
  };
  const branded = `${String(post.title || '').trim()} | Utah Realty Photos`;
  return {
    title: post.metaTitle ? clamp(post.metaTitle, 60) : (branded.length <= 60 ? branded : clamp(post.title, 60)),
    description: clamp(post.metaDescription || post.excerpt, 160),
    canonical: canonicalFor(`/blog/${post.slug}`),
    h1: post.title
  };
}

export const NOT_FOUND_META = {
  title: 'Page Not Found | Utah Realty Photos',
  description: 'That page doesn’t exist. Find real estate photography packages, prices, service areas and booking on the Utah Realty Photos home page.',
  h1: 'Page not found'
};

/** Service schema for a city page (shared with LocationPage). */
export function locationSchema(loc) {
  const url = canonicalFor(`/locations/${loc.slug}`);
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Real Estate Photography',
    name: `Real Estate Photography in ${loc.name}, Utah`,
    description: `Real estate photography, aerial, twilight, video and 3D tours for listings in ${loc.name}, ${loc.county}.`,
    url,
    provider: { '@type': 'ProfessionalService', '@id': SITE, name: 'Utah Realty Photos', url: SITE, telephone: '+1-801-210-0281', email: 'info@utahrealtyphotos.com' },
    areaServed: { '@type': 'City', name: loc.name, containedInPlace: { '@type': 'AdministrativeArea', name: loc.county } }
  };
}

/** BreadcrumbList for a path ([{name, url}] in order). */
export function breadcrumbSchema(crumbs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((c, i) => ({ '@type': 'ListItem', position: i + 1, name: c.name, item: c.url }))
  };
}

const CRUMB_NAMES = {
  '/services': 'Services', '/pricing': 'Pricing', '/faq': 'FAQ', '/portfolio': 'Portfolio',
  '/contact': 'Contact', '/book': 'Book', '/credit': 'Credit', '/blog': 'Blog',
  '/locations': 'Service Areas', '/about': 'About',
  '/privacy-policy': 'Privacy Policy', '/cookie-policy': 'Cookie Policy'
};

/** Breadcrumb trail for a public path ([] for the homepage or an unknown path). */
export function breadcrumbsFor(path) {
  const p = String(path || '/').split(/[?#]/)[0].replace(/(.)\/+$/, '$1') || '/';
  const home = { name: 'Home', url: `${SITE}/` };
  if (CRUMB_NAMES[p]) return [home, { name: CRUMB_NAMES[p], url: canonicalFor(p) }];
  let m;
  if ((m = p.match(/^\/services\/([a-z0-9-]+)$/)) && SERVICE_DETAIL[m[1]]) {
    return [home, { name: 'Services', url: canonicalFor('/services') }, { name: SERVICE_DETAIL[m[1]].title, url: canonicalFor(p) }];
  }
  if ((m = p.match(/^\/locations\/([a-z0-9-]+)$/)) && getLocation(m[1])) {
    return [home, { name: 'Service Areas', url: canonicalFor('/locations') }, { name: getLocation(m[1]).name, url: canonicalFor(p) }];
  }
  return [];
}

/** WebPage block for the homepage (the business itself is the index.html block). */
export function homePageSchema() {
  const m = STATIC_ROUTES['/'];
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Home - Utah Realty Photos',
    description: m.description,
    url: `${SITE}/`,
    mainEntity: { '@type': 'ProfessionalService', '@id': SITE, name: 'Utah Realty Photos' }
  };
}

/** ItemList of the city pages, for /locations. */
export function locationsListSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Utah Realty Photos service areas',
    description: 'Cities and areas served by Utah Realty Photos.',
    url: canonicalFor('/locations'),
    numberOfItems: LOCATIONS.length,
    itemListElement: LOCATIONS.map((loc, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: `Real Estate Photography in ${loc.name}, Utah`,
      url: canonicalFor(`/locations/${loc.slug}`)
    }))
  };
}

/**
 * Old public paths the app already sends elsewhere (App.js <Navigate>); the
 * server answers them with a 301 so search follows the same move.
 */
export const STATIC_REDIRECTS = {
  '/subscriptions': '/credit'
};

/**
 * App screens the server never renders: portals, admin, private links, and the
 * share-card routes (2026-10-17). vercel.json's catch-all rewrite excludes the
 * same first segments (the prerender suite checks the two lists agree), so
 * these keep getting the plain app shell exactly as before.
 */
export const APP_ROUTE_SEGMENTS = [
  'api', 'static', 'admin', 'client', 'photographer', 'gallery', 'property', 'property-info',
  'pay', 'payment', 'booking', 'review', 'unsubscribe', 'customize', 'reset-password',
  'verify-email', 'blog-preview'
];

export function isAppRoute(path) {
  const first = String(path || '/').split(/[?#]/)[0].split('/')[1] || '';
  return APP_ROUTE_SEGMENTS.includes(first);
}
